/**
 * @engine/vfx — Update stage generator.
 *
 * Emits the Slang module that advances live particles each frame. GPU
 * semantics ported verbatim from v1: alive gate, lifetime advance, node calls,
 * velocity integration, event triggers (context settings + GPU Event nodes),
 * and death → dead-list return (+ on-die events).
 */

import type { VFXGraph } from '../../VFXGraph';
import type { VFXNodeInstance } from '../../types';
import type { VFXExtractedSystem, VFXCompilerOptions } from '../types';
import { CallEmitter } from '../codegen/CallEmitter';
import { NodeFunctionCache } from '../codegen/NodeFunctionEmitter';
import { formatFloat } from '../codegen/slangTypes';
import { TWEAK_CAPACITY, type TweakAllocator } from '../codegen/Tweaks';
import {
  UPDATE_STAGE_BUILTINS,
  assembleStageModule,
  block,
  eventEmitLines,
} from './common';
import type { StageResult } from './initStage';

interface EventConfig {
  triggerType: string;
  triggerNode: VFXNodeInstance | null;
  eventId: number;
  spawnCount: number;
}

export function generateUpdateStage(
  system: VFXExtractedSystem,
  graph: VFXGraph,
  options: VFXCompilerOptions,
  /** Editor tweak mode: per-system allocator shared with the init stage. */
  tweaks: TweakAllocator | null = null
): StageResult {
  const workgroupSize = options.workgroupSize ?? 64;
  const updateNodes = system.update.nodes;
  const trigger = system.update.trigger;

  // Trigger and GPU Event nodes are stage concerns, not node calls.
  const regularNodes = updateNodes.filter(
    n => !n.type.startsWith('vfx.trigger.') && n.type !== 'vfx.output.gpu_event'
  );

  // GPU Event nodes and their connected triggers (v1 semantics)
  const gpuEventNodes = updateNodes.filter(n => n.type === 'vfx.output.gpu_event');
  const edges = graph.getEdges();
  const eventConfigs: EventConfig[] = [];
  let nextEventId = 0;
  for (const gpuEventNode of gpuEventNodes) {
    const triggerEdge = edges.find(
      e => e.to.nodeId === gpuEventNode.id && e.to.portId === 'trigger'
    );
    let triggerNode: VFXNodeInstance | null = null;
    let triggerType = 'vfx.trigger.on_die';
    if (triggerEdge) {
      triggerNode = updateNodes.find(n => n.id === triggerEdge.from.nodeId) ?? null;
      triggerType = triggerNode?.type ?? 'vfx.trigger.on_die';
    }
    eventConfigs.push({
      triggerType,
      triggerNode,
      eventId: nextEventId++,
      spawnCount: Number(triggerNode?.inputValues?.count) || 1,
    });
  }

  const emitter = new CallEmitter(graph, new NodeFunctionCache(), UPDATE_STAGE_BUILTINS, tweaks);
  emitter.emitNodes(regularNodes);
  const emission = emitter.result();

  const main: string[] = [];

  main.push('let idx = id.x;');
  main.push('if (idx >= uniforms.maxParticles) { return; }');
  main.push('');
  main.push('var particle = particles[idx];');
  main.push('if (particle.alive == 0u) { return; }');
  main.push('');
  main.push('let deltaTime = uniforms.deltaTime;');
  main.push('let simulationTime = uniforms.time;');
  if (emission.builtinsUsed.has('emitterVelocity')) {
    main.push('let emitterVelocity = float3(uniforms.emitterVelX, uniforms.emitterVelY, 0.0);');
  }

  if (trigger?.mode === 'over_distance') {
    main.push('let prevPosition = particle.position;');
  }
  main.push('');

  main.push('// Advance lifetime');
  main.push('particle.lifetime += deltaTime;');
  main.push('');

  let emissionStartIndex = -1;
  if (emission.lines.length > 0) {
    main.push('// Update nodes');
    emissionStartIndex = main.length;
    main.push(...emission.lines);
    main.push('');
  }

  main.push('// Integrate velocity');
  main.push('particle.position += particle.velocity * deltaTime;');
  main.push('');
  // Note: rotation is NOT integrated automatically — use an Update Rotation
  // node. Keeps behavior explicit (matches v1).

  // Context-settings trigger: over_time
  if (trigger?.mode === 'over_time') {
    const rate = trigger.rate ?? 10;
    const eventId = trigger.eventId ?? 1;
    main.push('// GPU Event over time (context setting)');
    main.push(`let eventChance = ${formatFloat(rate)} * deltaTime;`);
    main.push('let randVal = hash(particle.seed + uint(simulationTime * 1000.0));');
    main.push('if (randVal < eventChance)');
    main.push(...eventEmitLines(eventId, 1));
    main.push('');
  }

  // Context-settings trigger: over_distance
  if (trigger?.mode === 'over_distance') {
    const distanceRate = trigger.distanceRate ?? 0.1;
    const eventId = trigger.eventId ?? 1;
    main.push('// GPU Event over distance (context setting)');
    main.push('let distTraveled = length(particle.position - prevPosition);');
    main.push(`let eventChance = distTraveled * ${formatFloat(distanceRate)};`);
    main.push('let randVal = hash(particle.seed + uint(simulationTime * 1000.0) + 12345u);');
    main.push('if (randVal < eventChance)');
    main.push(...eventEmitLines(eventId, 1));
    main.push('');
  }

  // Per-frame events from GPU Event nodes
  for (const config of eventConfigs) {
    const { triggerType, triggerNode, eventId } = config;
    if (triggerType === 'vfx.trigger.over_time') {
      const rate = Number(triggerNode?.inputValues?.rate) || 10;
      main.push(`// GPU Event ${eventId} (Over Time, rate=${rate})`);
      main.push('{');
      main.push(...block([
        `let eventChance = ${formatFloat(rate)} * deltaTime;`,
        `let randVal = hash(particle.seed + uint(simulationTime * 1000.0) + ${eventId * 12345}u);`,
        'if (randVal < eventChance)',
        ...eventEmitLines(eventId, 1),
      ]));
      main.push('}');
      main.push('');
    } else if (triggerType === 'vfx.trigger.over_distance') {
      const distanceRate = Number(triggerNode?.inputValues?.distanceRate) || 0.1;
      main.push(`// GPU Event ${eventId} (Over Distance, rate=${distanceRate})`);
      main.push('{');
      main.push(...block([
        'let distTraveled = length(particle.velocity) * deltaTime;',
        `let eventChance = distTraveled * ${formatFloat(distanceRate)};`,
        `let randVal = hash(particle.seed + uint(simulationTime * 1000.0) + ${eventId * 54321}u);`,
        'if (randVal < eventChance)',
        ...eventEmitLines(eventId, 1),
      ]));
      main.push('}');
      main.push('');
    }
  }

  // Death
  main.push('// Particle death');
  main.push('if (particle.lifetime >= particle.maxLifetime)');
  main.push('{');
  main.push('    particle.alive = 0u;');
  main.push('    // Return the slot to the dead list (bounds-checked)');
  main.push('    let deadIdx = counters[0].deadCount.add(1u);');
  main.push('    if (deadIdx < uniforms.maxParticles)');
  main.push('    {');
  main.push('        deadList[deadIdx] = idx;');
  main.push('    }');

  // On-die trigger from context settings
  if (trigger?.mode === 'on_die') {
    main.push('');
    main.push('    // GPU Event on death (context setting)');
    main.push(...block(eventEmitLines(trigger.eventId ?? 1, trigger.count ?? 1)));
  }

  // On-die events from GPU Event nodes
  for (const config of eventConfigs) {
    if (config.triggerType === 'vfx.trigger.on_die') {
      main.push('');
      main.push(`    // GPU Event ${config.eventId} (On Die)`);
      main.push(...block(eventEmitLines(config.eventId, config.spawnCount)));
    }
  }

  // On-die event from system.outputEvent (On Die → Event Transit), unless
  // already handled above.
  const hasOnDieFromConfigs = eventConfigs.some(c => c.triggerType === 'vfx.trigger.on_die');
  const hasOnDieFromTrigger = trigger?.mode === 'on_die';
  const outputEvent = system.outputEvent as
    | (typeof system.outputEvent & { triggerType?: string })
    | undefined;
  if (outputEvent && !hasOnDieFromConfigs && !hasOnDieFromTrigger) {
    const triggerType = outputEvent.triggerType ?? 'vfx.trigger.on_die';
    if (triggerType === 'vfx.trigger.on_die') {
      main.push('');
      main.push(`    // GPU Event ${outputEvent.eventId ?? 0} (On Die → Event Transit)`);
      main.push(...block(eventEmitLines(outputEvent.eventId ?? 0, outputEvent.spawnCount ?? 1)));
    }
  }

  main.push('}');
  main.push('else');
  main.push('{');
  main.push('    counters[0].aliveCount.add(1u);');
  main.push('}');
  main.push('');
  main.push('particles[idx] = particle;');

  const assembled = assembleStageModule({
    attributes: options.attributes,
    emission,
    mainLines: main,
    workgroupSize,
    emissionStartIndex,
    tweakCapacity: tweaks ? TWEAK_CAPACITY : 0,
  });

  return {
    source: assembled.source,
    sourceMap: assembled.sourceMap,
    warnings: emission.warnings,
  };
}
